import {
  Button,
  Center,
  FormControl,
  FormLabel,
  HStack,
  VStack,
  Spinner,
  Switch,
  Textarea,
  useToast,
  Text,
  Box,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { Category, CategoryInList } from 'src/api/v1-api';
import useRequest from 'src/utils/hooks/useRequest';

interface AttributeImporterProps {
  category?: CategoryInList | Category | null;
  handleCreateAttributes?: (json: object) => Promise<void>;
  disableCreateBtn?: boolean;
  missingJson?: boolean;
  handleSetJson?: (json: object) => Promise<void>;
}

const AttributeImporter = ({
  category,
  handleCreateAttributes,
  disableCreateBtn = false,
  missingJson = false,
  handleSetJson,
}: AttributeImporterProps) => {
  const [completeOverwrite, setCompleteOverwrite] = useState(false);
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryJson, setCategoryJson] = useState('');
  const [hasDescriptionError, setHasDescriptionError] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { api } = useRequest();
  const toast = useToast();

  const handleGenerateAttributes = async () => {
    setIsGenerating(true);

    if (categoryDescription.length < 10) {
      setHasDescriptionError(true);
      toast({
        status: 'error',
        duration: 5000,
        isClosable: true,
        title: 'Please enter a meaningful description',
      });
      setIsGenerating(false);
      return;
    }

    setHasDescriptionError(false);

    const requestBody = {
      description: categoryDescription,
      complete_overwrite: completeOverwrite,
      ...(category?.id && { category_id: category.id }),
    };

    try {
      const response = await api.marketplace.categories.generate(requestBody);
      const jsonString = JSON.stringify(response, undefined, 4);

      setCategoryJson(jsonString);
      handleSetJson?.(JSON.parse(jsonString));

      toast({
        status: 'success',
        duration: 5000,
        isClosable: true,
        title: 'Attributes generated successfully',
      });
    } catch (error) {
      toast({
        status: 'error',
        duration: 5000,
        isClosable: true,
        title: 'Error generating attributes',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const parseJsonSafely = () => {
    try {
      return JSON.parse(categoryJson);
    } catch (error) {
      console.error('JSON parsing error:', error);
      return {};
    }
  };

  const handleCreateClick = () => {
    if (handleCreateAttributes) {
      handleCreateAttributes(parseJsonSafely());
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <Text fontSize="lg" fontWeight="semibold" mb={4}>
          Generate Attributes
        </Text>

        <FormControl isInvalid={hasDescriptionError}>
          <FormLabel fontSize="sm" fontWeight="medium">
            Category Description
          </FormLabel>
          <Textarea
            placeholder="Describe the category to generate relevant attributes..."
            value={categoryDescription}
            onChange={(e) => setCategoryDescription(e.target.value)}
            minH="100px"
            resize="vertical"
          />
        </FormControl>
      </Box>

      <Box>
        <Flex align="center" gap={4} mb={4}>
          {!disableCreateBtn && (
            <HStack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Only add new attributes
              </Text>
              <Switch
                isChecked={completeOverwrite}
                onChange={() => setCompleteOverwrite(!completeOverwrite)}
                isDisabled={isGenerating}
                colorScheme="blue"
              />
              <Text fontSize="sm" color="gray.600">
                Complete overwrite
              </Text>
            </HStack>
          )}

          <Spacer />

          <Button
            onClick={handleGenerateAttributes}
            isDisabled={isGenerating}
            isLoading={isGenerating}
            loadingText="Generating..."
            colorScheme="blue"
            size="md"
          >
            {completeOverwrite ? 'Generate Category' : 'Generate Attributes'}
          </Button>
        </Flex>
      </Box>

      <Box>
        <Flex align="center" justify="space-between" mb={3}>
          <FormLabel fontSize="sm" fontWeight="medium" mb={0}>
            Generated JSON
          </FormLabel>

          {!disableCreateBtn && handleCreateAttributes && (
            <Button
              onClick={handleCreateClick}
              isDisabled={isGenerating || !categoryJson.trim()}
              size="sm"
              colorScheme="green"
              variant="outline"
            >
              {completeOverwrite ? 'Create Category' : 'Create Attributes'}
            </Button>
          )}
        </Flex>

        {isGenerating ? (
          <Center py={8}>
            <VStack spacing={3}>
              <Spinner size="lg" color="blue.500" />
              <Text fontSize="sm" color="gray.600">
                Generating attributes...
              </Text>
            </VStack>
          </Center>
        ) : (
          <FormControl isInvalid={missingJson}>
            <Textarea
              placeholder="Generated JSON will appear here..."
              value={categoryJson}
              onChange={(e) => setCategoryJson(e.target.value)}
              minH="200px"
              resize="vertical"
              fontFamily="mono"
              fontSize="sm"
            />
          </FormControl>
        )}
      </Box>
    </VStack>
  );
};

export default AttributeImporter;
